import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createLead, deleteLead, fetchFilterOptions, fetchLeadStats, fetchLeads, updateLead,
  sendLeadAccessLink,
} from '../lib/api';
import FilterPanel, { countActiveFilters, FILTER_KEYS } from '../components/FilterPanel';
import { useAuth } from '../lib/auth';

const SOURCE_OPTS = [
  { id: 'manuale', label: 'Manuali (CRM)' },
  { id: 'getrix',  label: 'Getrix' },
  { id: 'tutti',   label: 'Tutti' },
];

// Estrae la parte "via" dall'indirizzo geocoded ("Via X, 04100 Comune LT, Italia" → "Via X")
const streetOnly = (addr) => (addr || '').split(',')[0].trim();
// URL alla pagina Immobili filtrata per quel singolo immobile
const immobileListUrl = (im) => `/app/immobili?q=${encodeURIComponent(streetOnly(im.indirizzo) || im.comune || '')}`;
// URL alla Mappa centrata su quell'immobile (raggio 1km)
const mappaUrl = (im) => {
  if (!im.lat || !im.lng) return `/app/mappa?q=${encodeURIComponent(streetOnly(im.indirizzo) || im.comune || '')}`;
  return `/app/mappa?center_lat=${im.lat}&center_lng=${im.lng}&radius_km=1&center_label=${encodeURIComponent(streetOnly(im.indirizzo) || im.comune || '')}`;
};
// Per annunci non in gestionale: Google Maps con l'indirizzo Getrix (non avendo lat/lng locali)
const mappaUrlExt = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

// Decide come aprire la mappa per un interest: sempre la mappa interna
// (per nostri usa il record immobile; per non-nostri usa le coords Getrix dell'annuncio;
// in extremis ripiega sull'indirizzo come query testuale).
function mapTarget(i) {
  if (i.immobile) return { href: mappaUrl(i.immobile), external: false, title: 'Vedi sulla nostra mappa' };
  if (i.annuncio_lat && i.annuncio_lng) {
    const label = i.annuncio_address || i.annuncio_location || '';
    return {
      href: `/app/mappa?center_lat=${i.annuncio_lat}&center_lng=${i.annuncio_lng}&radius_km=1&center_label=${encodeURIComponent(label)}`,
      external: false, title: 'Vedi sulla nostra mappa',
    };
  }
  if (i.annuncio_address) return { href: `/app/mappa?q=${encodeURIComponent(streetOnly(i.annuncio_address))}`, external: false, title: 'Vedi sulla nostra mappa' };
  return null;
}

function MapLink({ target, size = 14 }) {
  if (target.external) {
    return <a href={target.href} target="_blank" rel="noreferrer" title={target.title} style={mapBtn}><IMapPin size={size} /></a>;
  }
  return <Link to={target.href} title={target.title} style={mapBtn}><IMapPin size={size} /></Link>;
}

// Riga annuncio: testo + viste; in tooltip va a capo (wrap=true), nella main card va in una riga (wrap=false)
function renderAnnuncio(i, wrap) {
  const views = (
    <span style={{ fontSize: 11, color: 'var(--fg-subtle)', marginLeft: 6 }}>
      · {i.total_activities || 0} {(i.total_activities || 0) === 1 ? 'visita' : 'visite'}
    </span>
  );
  if (i.immobile) {
    const addr = (i.immobile.indirizzo || '') + (i.immobile.comune && !(i.immobile.indirizzo || '').includes(i.immobile.comune) ? `, ${i.immobile.comune}` : '');
    return (
      <>
        <Link to={immobileListUrl(i.immobile)}
              style={wrap ? addrLinkWrap : addrLink}
              title={addr}>
          {addr}
        </Link>
        {i.annuncio_is_auction ? ' 🔨' : ''}
        {views}
      </>
    );
  }
  // Non-nostro nel gestionale (ma annuncio pubblicato da noi su Getrix):
  // mostra l'indirizzo completo se l'importer l'ha salvato, altrimenti solo la città.
  const label = i.annuncio_address || i.annuncio_location || '—';
  return (
    <span style={{ ...(wrap ? addrTextWrap : addrText), color: 'var(--fg)' }}>
      {label}
      <em style={{ fontSize: 11, marginLeft: 4, color: 'var(--fg-subtle)' }}>(non in gestionale · Getrix #{i.getrix_annuncio_id})</em>
      {i.annuncio_price ? ` · ${i.annuncio_price}` : ''}
      {i.annuncio_is_auction ? ' 🔨' : ''}
      {views}
    </span>
  );
}

const Icon = ({ size = 14, stroke = 1.75, className = '', children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">{children}</svg>
);
const ISearch = (p) => (<Icon {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Icon>);
const IPlus = (p) => (<Icon {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Icon>);
const IEdit = (p) => (<Icon {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></Icon>);
const ITrash = (p) => (<Icon {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></Icon>);
const IX = (p) => (<Icon {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Icon>);
const IMail = (p) => (<Icon {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></Icon>);
const IPhone = (p) => (<Icon {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" /></Icon>);
const IMapPin = (p) => (<Icon {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></Icon>);
const ILink = (p) => (<Icon {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Icon>);
const ITable = (p) => (<Icon {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /></Icon>);

// Un lead ha criteri utili se criteriaToSearchParams produce almeno un parametro.
function leadHasCriteria(lead) {
  const sp = criteriaToSearchParams(lead?.criteria);
  for (const _ of sp.keys()) return true;
  return false;
}

// URL alla Mappa / Tabella immobili pre-filtrata con i criteri del lead.
// Riusa criteriaToSearchParams (il vocabolario condiviso col FilterPanel).
function leadMappaUrl(lead) {
  const sp = criteriaToSearchParams(lead?.criteria);
  if (!sp.get('view')) sp.set('view', 'valutazione');
  // center_label leggibile col nome comune, se non già presente (come mappaUrl)
  if (!sp.get('center_label') && lead?.criteria?.comune) {
    const comune = Array.isArray(lead.criteria.comune)
      ? lead.criteria.comune.join(', ')
      : String(lead.criteria.comune);
    sp.set('center_label', comune);
  }
  return `/app/mappa?${sp.toString()}`;
}
function leadTabellaUrl(lead) {
  const sp = criteriaToSearchParams(lead?.criteria);
  return `/app/immobili?${sp.toString()}`;
}

// Coppia di scorciatoie "Mappa" / "Tabella" per aprire le viste immobili
// pre-filtrate coi criteri del lead. Se il lead non ha criteri → disabilitate.
function LeadShortcuts({ lead, size = 13 }) {
  const enabled = leadHasCriteria(lead);
  if (!enabled) {
    const disabledTitle = 'Nessun criterio impostato per questo lead';
    return (
      <>
        <button type="button" className="im-act-btn" title={disabledTitle} disabled
                style={{ opacity: 0.4, cursor: 'not-allowed' }}>
          <IMapPin size={size} />
        </button>
        <button type="button" className="im-act-btn" title={disabledTitle} disabled
                style={{ opacity: 0.4, cursor: 'not-allowed' }}>
          <ITable size={size} />
        </button>
      </>
    );
  }
  return (
    <>
      <Link to={leadMappaUrl(lead)} className="im-act-btn"
            title="Apri mappa immobili filtrata coi criteri di questo lead">
        <IMapPin size={size} />
      </Link>
      <Link to={leadTabellaUrl(lead)} className="im-act-btn"
            title="Apri tabella immobili filtrata coi criteri di questo lead">
        <ITable size={size} />
      </Link>
    </>
  );
}

const TIPO_OPTS = [
  { id: 'investitore', label: 'Investitore' },
  { id: 'privato', label: 'Privato' },
  { id: 'studio', label: 'Studio legale' },
  { id: 'altro', label: 'Altro' },
];

const STATUS_OPTS = [
  { id: 'nuovo',       label: 'Nuovo',       color: 'oklch(0.55 0.18 260)' },
  { id: 'qualificato', label: 'Qualificato', color: 'oklch(0.55 0.13 200)' },
  { id: 'attivo',      label: 'Attivo',      color: 'oklch(0.55 0.13 150)' },
  { id: 'freddo',      label: 'Freddo',      color: 'oklch(0.55 0.04 250)' },
  { id: 'perso',       label: 'Perso',       color: 'oklch(0.55 0.18 25)' },
  { id: 'cliente',     label: 'Cliente',     color: 'oklch(0.45 0.18 290)' },
];

const fmtEuro = (n) => n != null
  ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
  : '—';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT'); } catch { return '—'; }
};

function StatusBadge({ status }) {
  const opt = STATUS_OPTS.find((s) => s.id === status) || STATUS_OPTS[0];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: opt.color, color: 'white', fontSize: 11, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>{opt.label}</span>
  );
}

function emptyForm() {
  return {
    name: '', surname: '', email: '', phone: '', codice_fiscale: '',
    tipo: 'privato', status: 'nuovo',
    categoria_target: 'entrambi',
    note: '',
  };
}

// criteria object → URLSearchParams (per FilterPanel)
function criteriaToSearchParams(criteria) {
  const sp = new URLSearchParams();
  if (!criteria) return sp;
  Object.entries(criteria).forEach(([k, v]) => {
    if (v == null || v === '' || v === false) return;
    if (Array.isArray(v)) {
      if (v.length > 0) sp.set(k, v.join(','));
    } else if (typeof v === 'boolean') {
      if (v) sp.set(k, 'true');
    } else {
      sp.set(k, String(v));
    }
  });
  return sp;
}

// URLSearchParams → criteria object (per save)
function searchParamsToCriteria(sp) {
  const obj = {};
  for (const k of FILTER_KEYS) {
    const v = sp.get(k);
    if (v == null || v === '' || v === 'false') continue;
    // multi (csv) vs scalar
    if (['comune', 'tribunale', 'piano', 'piano_categoria', 'liquidita_tier', 'livello_ristrutturazione',
         'disponibilita', 'tipologia_categoria', 'tipologia_escludi',
         'piano_escludi', 'piano_categoria_escludi', 'disponibilita_escludi'].includes(k)) {
      obj[k] = v.split(',').filter(Boolean);
    } else if (['dentro_gra', 'nuovi', 'scadute'].includes(k)) {
      obj[k] = true;
    } else if (['prezzo_max', 'mq_min', 'mq_max', 'center_lat', 'center_lng', 'radius_km'].includes(k)) {
      obj[k] = Number(v);
    } else if (k === 'center_label') {
      obj[k] = v;
    } else {
      obj[k] = v;
    }
  }
  return obj;
}

export function LeadEditor({ lead, onClose, onSaved, filterOptions }) {
  const isEdit = !!lead?.id;
  const [form, setForm] = useState(() => {
    if (!isEdit) return emptyForm();
    return {
      name: lead.name || '',
      surname: lead.surname || '',
      email: lead.email || '',
      phone: lead.phone || '',
      codice_fiscale: lead.codice_fiscale || '',
      tipo: lead.tipo || 'privato',
      status: lead.status || 'nuovo',
      categoria_target: lead.categoria_target || 'entrambi',
      note: lead.note || '',
    };
  });
  // Criteri immobili = stessi filtri della pagina /app/immobili (riusa FilterPanel)
  const [criteriaParams, setCriteriaParams] = useState(() =>
    criteriaToSearchParams(lead?.criteria || {})
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);

  // Quando arriva un errore di validazione (banner in cima al form), scrolla
  // automaticamente verso di esso — sul mobile l'utente è quasi sempre
  // scrollato in fondo al form al momento del submit e non lo vedrebbe.
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        criteria: searchParamsToCriteria(criteriaParams),
      };
      const saved = isEdit ? await updateLead(lead.id, payload) : await createLead(payload);
      onSaved(saved);
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.entries(err.response.data.errors).map(([k, v]) => `${k}: ${v[0]}`).join(' · ')
        : err?.response?.data?.message || 'Errore salvataggio';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const activeCriteriaCount = countActiveFilters(criteriaParams);
  const criteriaSummary = useMemo(() => {
    const parts = [];
    if (criteriaParams.get('comune')) parts.push(`📍 ${criteriaParams.get('comune').split(',').slice(0, 3).join(', ')}${criteriaParams.get('comune').split(',').length > 3 ? '…' : ''}`);
    if (criteriaParams.get('prezzo_max')) parts.push(`💶 ≤ €${Number(criteriaParams.get('prezzo_max')).toLocaleString('it-IT')}`);
    const mqMin = criteriaParams.get('mq_min'), mqMax = criteriaParams.get('mq_max');
    if (mqMin || mqMax) parts.push(`📐 ${mqMin || '?'}–${mqMax || '?'} mq`);
    if (criteriaParams.get('tipologia_categoria')) parts.push(`🏠 ${criteriaParams.get('tipologia_categoria')}`);
    if (criteriaParams.get('dentro_gra') === 'true') parts.push('GRA Roma');
    return parts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaParams.toString()]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fp__backdrop" onClick={onClose}>
      <aside className="fp__panel" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="fp__head">
          <div>
            <div className="fp__title">{isEdit ? 'Modifica lead' : 'Nuovo lead'}</div>
            <div className="fp__sub">{isEdit ? `id #${lead.id}` : 'Crea un nuovo lead'}</div>
          </div>
          <button type="button" onClick={onClose} className="fp__close" aria-label="Chiudi">
            <IX size={16} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="fp__body" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {error && (
            <div ref={errorRef} style={{
              background: 'oklch(0.96 0.04 25)', border: '1px solid oklch(0.75 0.12 25)',
              color: 'oklch(0.35 0.15 25)', padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
              marginBottom: 12,
            }}>{error}</div>
          )}

          <section className="fp__section" style={{ paddingTop: 0 }}>
            <h3 className="fp__section-title">Anagrafica</h3>
            <div className="fp__range">
              <div className="fp__field">
                <label className="fp__lbl">Nome *</label>
                <input className="fp__input" required value={form.name} onChange={(e) => set('name')(e.target.value)} />
              </div>
              <div className="fp__field">
                <label className="fp__lbl">Cognome</label>
                <input className="fp__input" value={form.surname} onChange={(e) => set('surname')(e.target.value)} />
              </div>
            </div>
            <div className="fp__field">
              <label className="fp__lbl">Email</label>
              <input className="fp__input" type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} />
            </div>
            <div className="fp__field">
              <label className="fp__lbl">Telefono</label>
              <input className="fp__input" type="tel" value={form.phone} onChange={(e) => set('phone')(e.target.value)} />
            </div>
            <div className="fp__field">
              <label className="fp__lbl">Codice fiscale</label>
              <input className="fp__input" maxLength={16} value={form.codice_fiscale}
                onChange={(e) => set('codice_fiscale')(e.target.value.toUpperCase())} />
            </div>
          </section>

          <section className="fp__section">
            <h3 className="fp__section-title">Classificazione</h3>
            <div className="fp__range">
              <div className="fp__field">
                <label className="fp__lbl">Tipo</label>
                <select className="fp__input" value={form.tipo} onChange={(e) => set('tipo')(e.target.value)}>
                  {TIPO_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              <div className="fp__field">
                <label className="fp__lbl">Stato</label>
                <select className="fp__input" value={form.status} onChange={(e) => set('status')(e.target.value)}>
                  {STATUS_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </section>

          <section className="fp__section">
            <h3 className="fp__section-title">Criteri immobili</h3>

            <div className="fp__field" style={{ marginBottom: 12 }}>
              <label className="fp__lbl">Categoria immobile cercata</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'residenziale', label: '🏠 Residenziale' },
                  { v: 'commerciale',  label: '🏪 Commerciale'  },
                  { v: 'entrambi',     label: 'Entrambi' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => set('categoria_target')(opt.v)}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      border: '1px solid ' + (form.categoria_target === opt.v ? 'oklch(0.55 0.20 250)' : 'oklch(0.85 0 0)'),
                      background: form.categoria_target === opt.v ? 'oklch(0.95 0.05 250)' : 'white',
                      color: form.categoria_target === opt.v ? 'oklch(0.30 0.18 250)' : 'oklch(0.30 0 0)',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: form.categoria_target === opt.v ? 600 : 400,
                      cursor: 'pointer',
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 10, lineHeight: 1.5 }}>
              Definisci i filtri usati per cercare immobili compatibili con questo lead.
              Sono gli stessi filtri della pagina <strong>Immobili</strong>: comune, tipologia,
              prezzo, mq, livello ristrutturazione, GRA, ecc.
            </p>
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => setFiltersOpen(true)}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>{activeCriteriaCount > 0 ? `Modifica criteri (${activeCriteriaCount})` : 'Imposta criteri di ricerca'}</span>
            </button>
            {criteriaSummary.length > 0 && (
              <div style={{
                marginTop: 10, padding: '10px 12px',
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 8, fontSize: 12, lineHeight: 1.7,
                display: 'flex', flexWrap: 'wrap', gap: '4px 12px',
              }}>
                {criteriaSummary.map((s, i) => <span key={i}>{s}</span>)}
              </div>
            )}
          </section>

          <section className="fp__section">
            <h3 className="fp__section-title">Note</h3>
            <textarea className="fp__input" rows={4} value={form.note}
              onChange={(e) => set('note')(e.target.value)}
              style={{ resize: 'vertical', fontFamily: 'inherit' }} />
          </section>
        </form>

        <footer className="fp__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn--solid" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Salvataggio…' : (isEdit ? 'Salva' : 'Crea lead')}
          </button>
        </footer>
      </aside>

      <FilterPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        searchParams={criteriaParams}
        setSearchParams={(next) => setCriteriaParams(
          next instanceof URLSearchParams ? next : new URLSearchParams(next)
        )}
        filterOptions={filterOptions}
        context="lead"
      />
    </div>
  );
}

export default function LeadPage() {
  const { user } = useAuth();
  const isB2c = !!user?.is_b2c;
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('manuale');
  const [soloNostri, setSoloNostri] = useState(false);
  const [tooltipLeadId, setTooltipLeadId] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('recent'); // recent | interactions
  const [hasEmail, setHasEmail] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);

  // Carica opzioni filtri (comuni, tribunali, ecc) una volta
  useEffect(() => {
    fetchFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { source: sourceFilter, page, per_page: 50, sort: sortBy };
      if (search) params.q = search;
      if (statusFilter && sourceFilter === 'manuale') params.status = statusFilter;
      if (soloNostri && sourceFilter !== 'manuale') params.solo_nostri = 1;
      if (hasEmail) params.has_email = 1;
      if (hasPhone) params.has_phone = 1;
      const [res, st] = await Promise.all([
        fetchLeads(params),
        fetchLeadStats({ source: sourceFilter }).catch(() => null),
      ]);
      setLeads(res.data || []);
      setMeta(res.meta || null);
      setStats(st);
    } catch (e) {
      setError(e?.message || 'Errore caricamento lead');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, sourceFilter, soloNostri, page, sortBy, hasEmail, hasPhone]);

  // Reset page quando cambia un filtro
  useEffect(() => { setPage(1); }, [search, statusFilter, sourceFilter, soloNostri, sortBy, hasEmail, hasPhone]);

  const handleSendAccessLink = async (lead) => {
    try {
      const res = await sendLeadAccessLink(lead.id);
      const url = res?.access_url || '';
      const phone = (lead.phone || '').replace(/\D/g, '');
      const msg = `Ciao${lead.name ? ' ' + lead.name : ''}, ti mando l'accesso personale all'area riservata per consultare le nostre analisi sugli immobili in asta:\n${url}\n\nValido 14 giorni. Al primo click sceglierai una password.`;
      if (phone) {
        const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
        try { await navigator.clipboard.writeText(url); } catch {}
        window.open(waUrl, '_blank');
      } else {
        try { await navigator.clipboard.writeText(url); window.alert('Link copiato negli appunti'); } catch {
          window.prompt('Copia il link:', url);
        }
      }
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Errore generazione link');
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Eliminare il lead "${lead.full_name}"?`)) return;
    try {
      await deleteLead(lead.id);
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      fetchLeadStats().then(setStats).catch(() => {});
    } catch (e) {
      window.alert('Errore eliminazione');
    }
  };

  const handleSaved = (saved) => {
    setLeads((ls) => {
      const i = ls.findIndex((l) => l.id === saved.id);
      if (i >= 0) { const next = [...ls]; next[i] = saved; return next; }
      return [saved, ...ls];
    });
    setEditing(null);
    fetchLeadStats().then(setStats).catch(() => {});
  };

  return (
    <main className="content content--inbox">
      <div className="page__header">
        <div className="page__title-wrap">
          <h1 className="page__title">Lead</h1>
          <p className="page__sub">
            {meta?.total != null
              ? `${meta.total.toLocaleString('it-IT')} lead`
              : 'Investitori e privati interessati a immobili giudiziari'}
          </p>
        </div>
        <div className="page__actions" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {meta && meta.last_page > 1 && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" className="btn btn--ghost" style={pagBtn}
                      disabled={page <= 1 || loading} onClick={() => setPage(1)}>«</button>
              <button type="button" className="btn btn--ghost" style={pagBtn}
                      disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)', padding: '0 6px', whiteSpace: 'nowrap' }}>
                {meta.current_page} / {meta.last_page}
              </span>
              <button type="button" className="btn btn--ghost" style={pagBtn}
                      disabled={page >= meta.last_page || loading} onClick={() => setPage((p) => p + 1)}>›</button>
              <button type="button" className="btn btn--ghost" style={pagBtn}
                      disabled={page >= meta.last_page || loading} onClick={() => setPage(meta.last_page)}>»</button>
            </div>
          )}
          {!isB2c && sourceFilter === 'manuale' && (
            <button type="button" className="btn btn--solid" onClick={() => setEditing('new')}>
              <IPlus size={13} />
              <span>Nuovo lead</span>
            </button>
          )}
        </div>
      </div>

      {/* Origine */}
      <div className="im-toolbar" style={{ marginBottom: 6 }}>
        <div className="mi-tabs" role="tablist">
          {SOURCE_OPTS.map((o) => (
            <button key={o.id} className="mi-tab" data-active={sourceFilter === o.id}
                    onClick={() => { setSourceFilter(o.id); setStatusFilter(''); }}>
              <span>{o.label}</span>
            </button>
          ))}
        </div>
        {sourceFilter !== 'manuale' && (
          <div className="im-toolbar__right">
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={soloNostri}
                     onChange={(e) => setSoloNostri(e.target.checked)} />
              Solo i nostri immobili
            </label>
          </div>
        )}
      </div>

      <div className="im-toolbar">
        {sourceFilter === 'manuale' ? (
          <div className="mi-tabs" role="tablist">
            <button className="mi-tab" data-active={statusFilter === ''} onClick={() => setStatusFilter('')}>
              <span>Tutti</span>
              {stats && <span className="mi-tab__count">{stats.total}</span>}
            </button>
            {STATUS_OPTS.map((o) => (
              <button key={o.id} className="mi-tab" data-active={statusFilter === o.id}
                      onClick={() => setStatusFilter(o.id)}>
                <span>{o.label}</span>
                {stats?.by_status?.[o.id] != null && (
                  <span className="mi-tab__count">{stats.by_status[o.id]}</span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="mi-tabs" role="tablist" style={{ flexWrap: 'wrap', gap: 8 }}>
            {/* Sort */}
            <button className="mi-tab" data-active={sortBy === 'recent'} onClick={() => setSortBy('recent')}>
              <span>Più recenti</span>
            </button>
            <button className="mi-tab" data-active={sortBy === 'interactions'} onClick={() => setSortBy('interactions')}>
              <span>Più interazioni</span>
            </button>
            <span style={{ width: 1, alignSelf: 'stretch', background: 'oklch(0.9 0 0)', margin: '0 4px' }} />
            {/* Filtri contatto */}
            <button className="mi-tab" data-active={hasEmail} onClick={() => setHasEmail(v => !v)}>
              <IMail size={12} /> <span>Email</span>
              {stats?.with_email != null && <span className="mi-tab__count">{stats.with_email}</span>}
            </button>
            <button className="mi-tab" data-active={hasPhone} onClick={() => setHasPhone(v => !v)}>
              <IPhone size={12} /> <span>Telefono</span>
              {stats?.with_phone != null && <span className="mi-tab__count">{stats.with_phone}</span>}
            </button>
          </div>
        )}
        <div className="im-toolbar__right">
          <div className="mi-search">
            <ISearch size={14} className="mi-search__icon" />
            <input type="text" className="mi-search__input"
              placeholder="Cerca nome, email, telefono…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {/* === Lista Getrix: card basse e larghe === */}
      {sourceFilter !== 'manuale' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading && <div className="mi-empty"><div className="mi-empty__title">Caricamento…</div></div>}
          {!loading && leads.length === 0 && (
            <div className="mi-empty">
              <div className="mi-empty__title">Nessun lead</div>
              <div className="mi-empty__body">
                {soloNostri ? 'Nessun lead agganciato ai nostri immobili.' : 'Nessun lead Getrix.'}
              </div>
            </div>
          )}
          {!loading && leads.map((l) => {
            const interests = l.interests || [];
            const sorted = [...interests].sort((a, b) => {
              if (!!a.immobile !== !!b.immobile) return a.immobile ? -1 : 1;
              return (b.total_activities || 0) - (a.total_activities || 0);
            });
            const first = sorted[0];
            const others = sorted.slice(1);
            const tooltipOpen = tooltipLeadId === l.id;
            return (
              <article key={l.id} className="getrix-card">
                {/* Anagrafica + contatti */}
                <div className="getrix-card__id">
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {l.name || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 2 }}>
                    {(l.interests_count || interests.length)} annunci · {(l.leads_count || 0)} {(l.leads_count || 0) === 1 ? 'visita' : 'visite'}
                  </div>
                  {l.email && (
                    <a href={`mailto:${l.email}`} style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12, color: 'var(--fg-muted)', textDecoration: 'none', marginTop: 2 }}>
                      <IMail size={11} /> <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.email}</span>
                    </a>
                  )}
                  {l.phone && (
                    <a href={`tel:${l.phone}`} style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12, color: 'var(--fg-muted)', textDecoration: 'none', fontFamily: 'var(--font-mono)' }}>
                      <IPhone size={11} /> {l.phone}
                    </a>
                  )}
                </div>

                {/* Immobile principale + "+altri" pill sotto */}
                <div className="getrix-card__main">
                  {first ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {renderAnnuncio(first, false)}
                        </div>
                        {mapTarget(first) && (
                          <MapLink target={mapTarget(first)} />
                        )}
                      </div>
                      {others.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <button type="button" style={pillBtn}
                                  onClick={() => setTooltipLeadId(tooltipOpen ? null : l.id)}>
                            +{others.length} altri
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Nessun annuncio</span>
                  )}

                  {tooltipOpen && others.length > 0 && (
                    <div style={tooltip}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <strong style={{ fontSize: 12 }}>Altri annunci ({others.length})</strong>
                        <button type="button" style={{ ...mapBtn, padding: 2 }} onClick={() => setTooltipLeadId(null)}>
                          <IX size={12} />
                        </button>
                      </div>
                      {others.map((i) => (
                        <div key={i.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '6px 0', borderTop: '1px solid oklch(0.94 0 0)' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {renderAnnuncio(i, true)}
                          </div>
                          {mapTarget(i) && (
                            <MapLink target={mapTarget(i)} size={13} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Edit + Invia link accesso */}
                <div className="getrix-card__edit" style={{ display: 'flex', gap: 4 }}>
                  <button type="button" className="im-act-btn" title="Invia link accesso (genera magic-link da inviare via WhatsApp)"
                          onClick={() => handleSendAccessLink(l)}>
                    <ILink size={14} />
                  </button>
                  <button type="button" className="im-act-btn" title="Modifica lead / aggiungi criteri"
                          onClick={() => setEditing(l)}>
                    <IEdit size={14} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}


      {/* === Desktop + mobile: vista CRM (source=manuale) === */}
      {sourceFilter === 'manuale' && (
      <>
      <div className="im-table-wrap">
        <div className="im-table-scroll">
          <table className="im-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th className="im-th im-th--left" style={{ width: 220 }}>Nome</th>
                <th className="im-th im-th--left" style={{ width: 110 }}>Tipo</th>
                <th className="im-th im-th--left" style={{ width: 110 }}>Stato</th>
                <th className="im-th im-th--left" style={{ width: 220 }}>Contatti</th>
                <th className="im-th im-th--right" style={{ width: 110 }}>Budget</th>
                <th className="im-th im-th--right" style={{ width: 90 }}>Mq</th>
                <th className="im-th im-th--left" style={{ width: 180 }}>Comuni target</th>
                <th className="im-th im-th--actions" style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8}>
                  <div className="mi-empty"><div className="mi-empty__title">Caricamento…</div></div>
                </td></tr>
              )}
              {error && !loading && (
                <tr><td colSpan={8}>
                  <div className="mi-empty">
                    <div className="mi-empty__title">Errore</div>
                    <div className="mi-empty__body">{error}</div>
                  </div>
                </td></tr>
              )}
              {!loading && !error && leads.length === 0 && (
                <tr><td colSpan={8}>
                  <div className="mi-empty">
                    <div className="mi-empty__title">Nessun lead</div>
                    <div className="mi-empty__body">
                      {search || statusFilter ? 'Nessun lead corrisponde ai filtri.' : 'Crea il tuo primo lead.'}
                    </div>
                  </div>
                </td></tr>
              )}
              {!loading && !error && leads.map((l) => (
                <tr key={l.id} className="im-tr">
                  <td className="im-td im-td--left">
                    <div style={{ fontWeight: 600, color: 'var(--fg)' }}>{l.full_name || l.name}</div>
                    {l.created_by?.name && (
                      <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 2 }}>
                        creato da {l.created_by.name}
                      </div>
                    )}
                  </td>
                  <td className="im-td im-td--left">
                    {TIPO_OPTS.find((t) => t.id === l.tipo)?.label || l.tipo || '—'}
                  </td>
                  <td className="im-td im-td--left">
                    <StatusBadge status={l.status} />
                  </td>
                  <td className="im-td im-td--left">
                    {l.email && (
                      <div style={{ marginBottom: 2 }}>
                        <a href={`mailto:${l.email}`} style={{ color: 'var(--fg)', textDecoration: 'none', fontSize: 12.5 }}>
                          {l.email}
                        </a>
                      </div>
                    )}
                    {l.phone && (
                      <div>
                        <a href={`tel:${l.phone}`} style={{ color: 'var(--fg-muted)', textDecoration: 'none', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                          {l.phone}
                        </a>
                      </div>
                    )}
                    {!l.email && !l.phone && <span className="im-na">—</span>}
                  </td>
                  <td className="im-td im-td--right">
                    {l.budget_max != null ? fmtEuro(l.budget_max) : <span className="im-na">—</span>}
                  </td>
                  <td className="im-td im-td--right">
                    {l.mq_min != null || l.mq_max != null
                      ? `${l.mq_min ?? '?'}–${l.mq_max ?? '?'}`
                      : <span className="im-na">—</span>}
                  </td>
                  <td className="im-td im-td--left" style={{ fontSize: 12 }}>
                    {l.comune_target?.length > 0
                      ? l.comune_target.slice(0, 3).join(', ') + (l.comune_target.length > 3 ? `, +${l.comune_target.length - 3}` : '')
                      : <span className="im-na">—</span>}
                  </td>
                  <td className="im-td im-td--actions">
                    <div className="im-actions">
                      <LeadShortcuts lead={l} size={13} />
                      <button type="button" className="im-act-btn" title="Modifica" onClick={() => setEditing(l)}>
                        <IEdit size={13} />
                      </button>
                      <button type="button" className="im-act-btn" title="Elimina"
                        onClick={() => handleDelete(l)}
                        style={{ color: 'oklch(0.55 0.18 25)' }}>
                        <ITrash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* === Mobile: card grid === */}
      <div className="im-cards im-cards--mobile-only">
        {loading && (
          <div className="mi-empty"><div className="mi-empty__title">Caricamento…</div></div>
        )}
        {error && !loading && (
          <div className="mi-empty">
            <div className="mi-empty__title">Errore</div>
            <div className="mi-empty__body">{error}</div>
          </div>
        )}
        {!loading && !error && leads.length === 0 && (
          <div className="mi-empty">
            <div className="mi-empty__title">Nessun lead</div>
            <div className="mi-empty__body">
              {search || statusFilter ? 'Nessun lead corrisponde ai filtri.' : 'Crea il tuo primo lead.'}
            </div>
          </div>
        )}
        {!loading && !error && leads.map((l) => (
          <article key={l.id} className="im-card">
            <header className="im-card__head">
              <div className="im-card__title">
                <div className="im-card__comune">{l.full_name || l.name}</div>
                {l.tipo && <div className="im-card__addr">{TIPO_OPTS.find((t) => t.id === l.tipo)?.label || l.tipo}</div>}
              </div>
              <StatusBadge status={l.status} />
            </header>

            <div className="im-card__chips">
              {l.email && (
                <a href={`mailto:${l.email}`} className="im-card__chip" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <IMail size={11} /> {l.email}
                </a>
              )}
              {l.phone && (
                <a href={`tel:${l.phone}`} className="im-card__chip" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <IPhone size={11} /> {l.phone}
                </a>
              )}
            </div>

            {(l.budget_max != null || l.mq_min != null || l.mq_max != null) && (
              <div className="im-card__numbers" style={{ gridTemplateColumns: '1fr 1fr', borderTop: 'none', paddingTop: 4 }}>
                {l.budget_max != null && (
                  <div className="im-card__num">
                    <div className="im-card__num-lbl">Budget</div>
                    <div className="im-card__num-val">{fmtEuro(l.budget_max)}</div>
                  </div>
                )}
                {(l.mq_min != null || l.mq_max != null) && (
                  <div className="im-card__num">
                    <div className="im-card__num-lbl">Mq</div>
                    <div className="im-card__num-val">
                      {l.mq_min ?? '?'} – {l.mq_max ?? '?'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {l.comune_target?.length > 0 && (
              <div className="im-card__meta">
                <span><strong>Comuni:</strong> {l.comune_target.join(', ')}</span>
              </div>
            )}

            {l.note && (
              <div className="im-card__meta" style={{ fontStyle: 'italic', maxHeight: 60, overflow: 'hidden' }}>
                "{l.note}"
              </div>
            )}

            <div className="im-card__meta" style={{ color: 'var(--fg-subtle)', fontSize: 11 }}>
              <span>Creato il {fmtDate(l.created_at)}{l.created_by?.name ? ` da ${l.created_by.name}` : ''}</span>
            </div>

            <div className="im-card__actions">
              {leadHasCriteria(l) ? (
                <>
                  <Link to={leadMappaUrl(l)} className="im-card__act"
                        title="Apri mappa immobili filtrata coi criteri di questo lead">
                    <IMapPin size={14} /> <span>Mappa</span>
                  </Link>
                  <Link to={leadTabellaUrl(l)} className="im-card__act"
                        title="Apri tabella immobili filtrata coi criteri di questo lead">
                    <ITable size={14} /> <span>Tabella</span>
                  </Link>
                </>
              ) : (
                <>
                  <span className="im-card__act" title="Nessun criterio impostato per questo lead"
                        style={{ opacity: 0.4, cursor: 'not-allowed' }}>
                    <IMapPin size={14} /> <span>Mappa</span>
                  </span>
                  <span className="im-card__act" title="Nessun criterio impostato per questo lead"
                        style={{ opacity: 0.4, cursor: 'not-allowed' }}>
                    <ITable size={14} /> <span>Tabella</span>
                  </span>
                </>
              )}
              <button type="button" className="im-card__act" onClick={() => setEditing(l)}>
                <IEdit size={14} /> <span>Modifica</span>
              </button>
              <button type="button" className="im-card__act"
                onClick={() => handleDelete(l)}
                style={{ color: 'oklch(0.55 0.18 25)' }}>
                <ITrash size={14} /> <span>Elimina</span>
              </button>
            </div>
          </article>
        ))}
      </div>
      </>
      )}

      {editing && (
        <LeadEditor
          lead={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
          filterOptions={filterOptions}
        />
      )}
    </main>
  );
}

const getrixCard = {
  display: 'flex', gap: 12, alignItems: 'center',
  background: 'white', border: '1px solid oklch(0.92 0 0)', borderRadius: 10,
  padding: '10px 12px', flexWrap: 'wrap',
};
const addrLink = {
  color: 'oklch(0.45 0.13 250)', textDecoration: 'none', fontSize: 13,
  display: 'inline-block', maxWidth: '100%',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const addrLinkWrap = {
  color: 'oklch(0.45 0.13 250)', textDecoration: 'none', fontSize: 13,
  display: 'inline', whiteSpace: 'normal', wordBreak: 'break-word',
};
const addrText = { fontSize: 13 };
const addrTextWrap = { fontSize: 13, whiteSpace: 'normal', wordBreak: 'break-word' };
const mapBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, border: '1px solid oklch(0.88 0 0)', borderRadius: 6,
  background: 'white', color: 'var(--fg-muted)', cursor: 'pointer', textDecoration: 'none',
};
const pillBtn = {
  padding: '4px 10px', border: '1px solid oklch(0.85 0 0)', borderRadius: 999,
  background: 'oklch(0.98 0 0)', fontSize: 11.5, cursor: 'pointer', color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};
const pagBtn = { minWidth: 30, padding: '4px 8px', fontSize: 13 };
const tooltip = {
  position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 10,
  background: 'white', border: '1px solid oklch(0.85 0 0)', borderRadius: 8,
  padding: '8px 10px', boxShadow: '0 6px 20px oklch(0 0 0 / 0.12)',
  minWidth: 280, maxWidth: 'min(420px, 90vw)',
};
